/**
 * Editable PDP accordion sections (Fragrance Notes, Ingredients, Delivery &
 * Shipping, Returns). Stored in products.attributes.pdpSections — no schema
 * change. Blank text hides the section; never-saved text falls back to the original storefront copy so
 * existing products keep their content.
 */
export type PdpSectionId = "notes" | "ingredients" | "shipping" | "returns";
export type PdpSection = { enabled: boolean; text: string };
export type PdpSections = Record<PdpSectionId, PdpSection>;

export const PDP_SECTION_LABELS: Record<PdpSectionId, string> = {
  notes: "Fragrance Notes",
  ingredients: "Ingredients",
  shipping: "Delivery & Shipping",
  returns: "Returns",
};

export function defaultPdpText(id: PdpSectionId, family = "floral"): string {
  switch (id) {
    case "ingredients":
      return `Alcohol Denat., Parfum (Fragrance), Aqua (Water), naturally derived isolates from the ${family.toLowerCase()} accord. Full INCI list available on the flacon. Never tested on animals.`;
    case "shipping":
      return [
        "Free Standard Delivery on qualifying orders — the threshold is shown at checkout.",
        "Dispatched from Mumbai within 24 hours on business days.",
        "Express Delivery available at checkout — 1 to 2 business days across India.",
      ].join("\n");
    case "returns":
      return "Unopened flacons may be returned within 30 days for a full refund. Sample vials and personalised engravings are non-returnable. Contact concierge@cb16.maison to initiate a return.";
    default:
      return "";
  }
}

/** Reads saved settings; anything missing defaults to visible + default copy. */
export function readPdpSections(attributes: Record<string, unknown> | undefined, family?: string): PdpSections {
  const raw = (attributes?.pdpSections ?? {}) as Record<string, unknown>;
  const pick = (id: PdpSectionId): PdpSection => {
    const v = (raw[id] ?? {}) as Record<string, unknown>;
    const text = typeof v.text === "string" ? v.text : defaultPdpText(id, family);
    return { enabled: v.enabled !== false, text };
  };
  return { notes: pick("notes"), ingredients: pick("ingredients"), shipping: pick("shipping"), returns: pick("returns") };
}
